import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Loader2 } from "lucide-react";

// Mirrors the design system's .btn-* classes (elixir-lu-ui/components.css). We
// re-express them as cva variants rather than applying the classes directly
// because the buttons here also need loading/size states the DS doesn't define.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 font-semibold transition-colors duration-200 cursor-pointer disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-900 focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-primary-900 text-on-primary hover:bg-primary-800",
        destructive: "bg-danger-900 text-on-primary hover:bg-danger-800",
        // .btn-secondary — outlined in primary, fills on hover.
        outline: "border border-primary-900 bg-white text-primary-900 hover:bg-primary-900 hover:text-on-primary",
        // .btn-danger — the danger sibling of outline. Never the primary CTA.
        danger: "border border-danger-900 bg-white text-danger-900 hover:bg-danger-900 hover:text-on-primary focus-visible:ring-danger-900",
        positive: "bg-green-700 text-white hover:bg-green-800",
        negative: "bg-danger-900 text-on-primary hover:bg-danger-800",
        ghost: "text-primary-900 bg-transparent hover:bg-gray-100",
        icon: "text-primary-900 bg-transparent hover:bg-gray-100",
      },
      size: {
        default: "px-4 py-2 text-sm rounded-lg",
        sm: "px-3 py-1.5 text-xs rounded-lg",
        lg: "px-6 py-3 text-base rounded-lg",
        icon: "p-2 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({ className, variant, size, loading, children, ref, ...props }) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export { Button, buttonVariants };
